import { Table, TableCell, TableContainer, TableRow, styled } from "@mui/material";
import type { TableCellProps, TableProps, TableRowProps } from "@mui/material";
import type { ReactNode } from "react";

export interface FinancialTableProps extends TableProps {
    children: ReactNode;
}

/**
 * Thin `TableContainer` + `Table` shell for financial statement tables (balance
 * sheet / income statement style). Mirrors `TableBase.tsx`'s shell but doesn't
 * enforce its `{ headers, body }` slot shape since financial tables are typically
 * composed inline (`<FinancialTable><TableHead>…</TableHead><TableBody>…</TableBody></FinancialTable>`).
 */
export const FinancialTable = ({ children, ...rest }: FinancialTableProps) => (
    <TableContainer>
        <Table size="small" {...rest}>
            {children}
        </Table>
    </TableContainer>
);

export interface FinancialTableRowProps extends TableRowProps {
    variant?: "default" | "section" | "subtotal";
}

export const FinancialTableRow = styled(TableRow, {
    shouldForwardProp: (prop) => prop !== "variant",
})<FinancialTableRowProps>(({ theme, variant = "default" }) => ({
    ...(variant === "section" && {
        backgroundColor: theme.palette.action.hover,
        "& .MuiTableCell-root": {
            fontWeight: 700,
            color: theme.palette.text.primary,
        },
    }),
    ...(variant === "subtotal" && {
        "& .MuiTableCell-root": {
            fontWeight: 700,
            color: theme.palette.text.primary,
            borderTop: `1px solid ${theme.palette.divider}`,
        },
    }),
}));

export interface FinancialTableCellProps extends TableCellProps {
    numeric?: boolean;
    indent?: boolean;
}

export const FinancialTableCell = styled(TableCell, {
    shouldForwardProp: (prop) => prop !== "numeric" && prop !== "indent",
})<FinancialTableCellProps>(({ theme, numeric, indent }) => ({
    ...(numeric && {
        textAlign: "right",
        fontVariantNumeric: "tabular-nums",
    }),
    ...(indent && {
        paddingLeft: theme.spacing(3.5),
    }),
}));
